import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, Params, Router } from "@angular/router";

import { ViewportScroller } from "@angular/common";
import { MatSnackBar } from "@angular/material/snack-bar";
import * as htmlToImage from "html-to-image";
import { cloneDeep } from "lodash";
import { NzNotificationService } from "ng-zorro-antd/notification";
import { Observable, Subject, forkJoin, of } from "rxjs";
import {
  catchError,
  debounceTime,
  filter,
  switchMap,
  take,
  takeUntil,
  tap,
} from "rxjs/operators";
import { JobDetail } from "../../../models/jobDetail";
import { MinMaxDialog } from "../../../models/minMaxDialog";
import { ModelSettings } from "../../../models/modelSettings";
import { ModelType } from "../../../models/modelType";
import { ModelViewSettings } from "../../../models/modelViewSetting";
import { Project } from "../../../models/project";
import { Ranges } from "../../../models/ranges";
import { ShotOverlayType } from "../../../models/shotOverlayType";
import { Slice } from "../../../models/slice";
import { SliceType } from "../../../models/sliceType";
import { SettingTypes } from "../../../shared/enums/settingTypes";
import { AuthService } from "../../../shared/services/auth.service";
import { CachedProjectService } from "../../../shared/services/cached-project.service";
import { CachedUserService } from "../../../shared/services/cached-user.service";
import { CachedPaletteService } from "../../services/cached-palette.service";
import {
  DisplaySettings,
  DisplaySettingsManager,
} from "../../services/display-settings-manager.service";
import { StoredSettingsService } from "../../services/stored-settings.service";
import {
  ColorBarTextInfo,
  stripDash,
  vpLowerBound,
  vpUpperBound,
} from "../chart-slice/chart-slice.component";
import { VelocityComponent } from "../dialogs/velocity/velocity.component";
import { allSliceProcessings } from "./slice_processings";

// Helper to track received slice ranges
interface SliceRangeData {
  inline: { min: number; max: number; trueMin: number; trueMax: number } | null;
  crossline: {
    min: number;
    max: number;
    trueMin: number;
    trueMax: number;
  } | null;
  depth: { min: number; max: number; trueMin: number; trueMax: number } | null;
}

declare var $: any;
@Component({
    selector: "app-model",
    templateUrl: "./model.component.html",
    styleUrls: ["./model.component.less"],
    animations: [
        trigger("fadeInOut", [
            transition(":enter", [
                // :enter is alias to 'void => *'
                style({ opacity: 0 }),
                animate(500, style({ opacity: 1 })),
            ]),
            transition(":leave", [
                // :leave is alias to '* => void'
                animate(500, style({ opacity: 0 })),
            ]),
        ]),
        trigger("flyInOut", [
            state("in", style({ opacity: 1, transform: "translateY(0)" })),
            transition("void => *", [
                style({
                    opacity: 0,
                    transform: "translateY(-100%)",
                }),
                animate("0.2s ease-in"),
            ]),
            transition("* => void", [
                animate("0.2s 0.1s ease-out", style({
                    opacity: 0,
                    transform: "translateY(100%)",
                })),
            ]),
        ]),
    ],
    standalone: false
})
export class ModelComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild("sliceSettings") sliceSettingsEl: ElementRef<HTMLDivElement>;
  @ViewChild("notificationTemplate", { static: false })
  newIterationsTemplate?: TemplateRef<{}>;

  previous_projectID: string = null;
  private lastJobId: string = null; // Track last job ID to detect job switches
  currentJob: JobDetail;
  height: number = 625;
  inputHeight: number = 625;
  inputDepthHeight = 250;
  settingsKey = "page";
  inlineNumber: number = null;
  crosslineNumber: number = null;
  depthSlice: number = null;
  iteration: number = null;
  timeSlice: number = null;
  availTimeSlices: number[] = [];
  showDepthSliceScreenshot: boolean = true;
  depthScreenshotText: string = "(w/o depth)";
  selectedJobs: any = null;
  shotId: number = null;
  availShots: number[] = [];
  timeSliceModels = ["ForwardWavefield", "BackwardWavefield"];
  shotModels = ["ForwardWavefield", "BackwardWavefield", "grad", "den", "dens"];
  selectedSonicId: any = null;

  private _rules: any = null;
  private _comment: string = null;
  private _name: string = null;
  private _searchId: any = null;
  private destroyed$ = new Subject<void>();

  steps: number[] = [10, 10, 10, 10];
  ranges: Ranges = {
    x: [0, 0],
    y: [0, 0],
    z: [0, 0],
  };
  slices: Slice[] = [
    {
      sliceType: SliceType.inline,
      span: 2,
    },
    {
      sliceType: SliceType.crossLine,
      span: 1,
    },
  ];

  isLoading: boolean = false;
  // for data changes - will toggle complete chart update and get the slice again
  reloadChartData: boolean = false;

  // for ui changes - will only toggle the chart update, redrawing with existing data
  relayoutChart: boolean = false;

  inlineWidth: number = 100;
  crosslineWidth: number = 100;

  maxDepth: number = 0;
  depthHeight: number = 250;
  palettes: string[] = [];
  modelTypes: ModelType[] = [
    { id: "Vp", name: "Vp", processing: [] },
    { id: "Gradient", name: "Gradient", processing: [] },
    { id: "InvVp Update", name: "InvVp Update", processing: [] },
  ];
  shotOverlayTypes: ShotOverlayType[] = [
    { id: "traces_fit", name: "Traces Fit", min: 0, max: 100 },
  ];
  horizonTypes: any = [];
  currentModelTypeId = null;
  currentModelTypeProcessing: Array<string> = [];
  currentShotOverlayType: ShotOverlayType = null;
  showSonicLog: boolean = false;
  showWellLog: boolean = false;
  showShotOverlay: boolean = false;
  showHorizonTypes = [];
  showHorizon: boolean = false;
  color: string = "#000000";
  horizonColors: string[] = [];
  projectHorizonColors: string[] = [];

  epochs: number;
  costPerIteration: number;
  hasCosts: boolean = true;
  elapsedTime: string;
  sliderBarOpen: boolean = false;
  sliderBarVisible: boolean = false;
  loadedJob = false;
  synchroniseSettings = false;
  vpUpdateCheck = false;
  vpUpdateBlockCheck = false;
  vpDiffIteration: number;

  isStaff = false;
  isMobile: boolean;

  displaySettings: DisplaySettings = {
    min: null,
    max: null,
    minZ: null,
    maxZ: null,
    isAuto: true,
    isAutoSynced: true,
    palette: null,
  };

  // Track the true min/max from each slice as they load
  private sliceRanges: SliceRangeData = {
    inline: null,
    crossline: null,
    depth: null,
  };

  velocities: number[];
  depth: number;
  verticalProfileAxis: number[] = [null, null];
  inlineColorBarMinMax: number[] = [null, null];
  verticalProfileIsLoading = true;
  verticalProfileHasError: boolean;
  velocityProfileCrossline: number;
  syncAxis: boolean;
  vpPositionSwapped = false;
  vpHeight: number;
  enableClick = false;
  scrollPos: number;
  restoredScrollPos: number;
  time_to_scroll_subscription = new Subject<string>();
  waitingFor: number;
  functionals;
  frequencies;
  additionals;
  dataSource: object[] = [];
  cols = ["Block", "Functional", "Frequency", "Iterations"];
  FUNCTIONAL_COLORS = {
    AWI: "#F8DC6F",
    FWI: "#EE865B",
    RWI: "#4295A5",
  };
  tableHeight = "300px";

  shotRadius = 2;
  showDottedLines = true;
  smoothingEnabled = true;
  fullWidthILXL = false;
  ILXLStartDepth = null;
  ILXLEndDepth = null;
  ILXLStartInline = null;
  ILXLEndInline = null;
  ILXLStartCrossline = null;
  ILXLEndCrossline = null;
  max_inline = null;
  max_crossline = null;
  showDepthSliceGraph = false;
  comments: string = "";
  screenshotWithVertProfile: boolean = false;
  modelInfoLoading: boolean = false;
  modelInfoFetched: boolean = false;
  reparsingJob: boolean = false;
  // syncRangeBetweenSlices: boolean = true;
  depthSliceWellLogs: { x: number; y: number }[] = [];
  projectData: Project;
  project_horizons: any[] = [];
  showProjectHorizonTypes: any = [];
  sonicLogId: any;
  private ilxlRangeTrigger$ = new Subject<void>();
  private depthRangeTrigger$ = new Subject<void>();
  private inlineSliceData: {
    sliceArray: number[][];
    trueMin: number;
    trueMax: number;
  } = null;
  previousJob: JobDetail;
  // i_sliceRanges: {[key: SliceType]: Ranges};
  i_sliceRanges: { [key: string]: Ranges } = {};
  i_sliceVelocityRanges: {};
  functional_sequence$: Observable<any>;

  constructor(
    public dialog: MatDialog,
    private snackbar: MatSnackBar,
    private projectService: CachedProjectService,
    private route: ActivatedRoute,
    private router: Router,
    private sanitizer: DomSanitizer,
    private paletteService: CachedPaletteService,
    public authService: AuthService,
    private storedSettingsService: StoredSettingsService,
    private userService: CachedUserService,
    private zone: NgZone,
    private notificationService: NzNotificationService,
    private settingsManager: DisplaySettingsManager,
    private scroller: ViewportScroller,
  ) {
    this.currentShotOverlayType = this.shotOverlayTypes[0];
  }

  ngOnInit(): void {
    this.time_to_scroll_subscription.pipe(take(1)).subscribe((res) => {
      if (this.restoredScrollPos != null) {
        window.scrollTo(0, this.restoredScrollPos);
        this.restoredScrollPos = null;
      }
    });
    this.isMobile =
      /Mobile/.test(navigator.userAgent) || window.innerWidth <= 600;

    this.paletteService
      .getPalettes()
      .pipe(takeUntil(this.destroyed$))
      .subscribe((palettes) => {
        this.palettes = palettes;
      });

    // Subscribe to sync changes from the parent component
    this.storedSettingsService.synchroniseSettings
      .pipe(takeUntil(this.destroyed$))
      .subscribe((val) => {
        this.synchroniseSettings = val;

        if (val == true) {
          if (
            this.displaySettings.min != null &&
            this.displaySettings.max != null &&
            this.displaySettings.minZ != null &&
            this.displaySettings.maxZ != null
          ) {
            this.displaySettings.isAuto = false;
            this.defineCurrentVelocityRange();
            this.persistDisplaySettings();
          }
        }
      });

    this.userService.userDetail
      .pipe(takeUntil(this.destroyed$))
      .subscribe((d) => {
        this.isStaff = d && d.isStaff;
      });

    this.projectService.currentJob
      .pipe(
        // 1. Wait for a valid job to be emitted from the service
        filter((job) => !!job),

        // 2. When a new job arrives, run our main setup logic and wait for projectData
        switchMap((job) => this.handleNewJobSetup(job)),

        // 3. After the setup is done, SWITCH to listening for query param changes.
        // This GUARANTEES that query params are only processed AFTER a job is loaded.
        switchMap(() => this.route.queryParams.pipe(debounceTime(100))),

        // 4. Now that we're listening to query params, handle them.
        tap((params) => this.updateFromQueryParams(params)),
        takeUntil(this.destroyed$),
      )
      .subscribe();

    // 1. Setup Inline/Crossline Stream
    this.ilxlRangeTrigger$
      .pipe(
        filter(
          () =>
            this.displaySettings.isAutoSynced &&
            this.inlineNumber != null &&
            this.crosslineNumber != null,
        ),
        switchMap(() => {
          const sliceParams = {
            inline: this.inlineNumber,
            crossline: this.crosslineNumber,
          };
          const shotParam = this._shouldSendShotParam() ? this.shotId : null;
          const timeSliceParam = this._shouldSendTimeSliceParam()
            ? this.timeSlice
            : null;
          return this.projectService
            .getRanges(
              this.currentJob.id,
              this.currentModelTypeId,
              this.iteration ?? 0,
              sliceParams,
              shotParam,
              timeSliceParam,
            )
            .pipe(
              catchError((err) => {
                console.error("Auto range error", err);
                return of(null);
              }),
            );
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((range) => {
        if (range) {
          this.displaySettings.min = range.min_val;
          this.displaySettings.max = range.max_val;
          this.sliceRanges.inline = this._buildSliceRange(
            range.min_val,
            range.max_val,
          );
          this.sliceRanges.crossline = this._buildSliceRange(
            range.min_val,
            range.max_val,
          );
          this.defineCurrentVelocityRange();
          this.persistDisplaySettings();
        }
      });

    // 2. Setup Depth Stream (Similar logic)
    this.depthRangeTrigger$
      .pipe(
        filter(() => !!this.depthSlice),
        switchMap(() => {
          if (this.showDepthSliceGraph)
            return this.projectService
              .getRanges(
                this.currentJob.id,
                this.currentModelTypeId,
                this.iteration ?? 0,
                { depth: this.depthSlice },
              )
              .pipe(
                catchError((err) => {
                  console.error("Depth auto range error", err);
                  return of(null);
                }),
              );
          else return of(null);
        }),
        takeUntil(this.destroyed$),
      )
      .subscribe((range) => {
        if (range) {
          this.displaySettings.minZ = range.min_val;
          this.displaySettings.maxZ = range.max_val;
          this.sliceRanges.depth = this._buildSliceRange(
            range.min_val,
            range.max_val,
          );
          this.defineCurrentVelocityRange();
          this.persistDisplaySettings();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.currentJob) {
      this.storeSettings();
      this.persistDisplaySettings();
    }
    this.loadedJob = false;

    this.destroyed$.next();
    this.destroyed$.complete();
  }

  @HostListener("window:scroll", ["$event"])
  onWindowScroll(e) {
    var offset = $("#slice-settings").offset();
    this.sliderBarVisible = offset && offset.top + 50 < $(document).scrollTop();
    this.scrollPos =
      window.pageYOffset ||
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      0;
  }

  ngAfterViewInit(): void {}

  private handleNewJobSetup(job: JobDetail): Observable<any> {
    // 1. Handle state persistence for the PREVIOUS job
    if (this.currentJob) {
      this.previousJob = cloneDeep(this.currentJob);

      this.storeSettings();
      this.persistDisplaySettings();

      // Reset component state if we are switching to a completely new project
      if (job.projectId !== this.currentJob.projectId) {
        this._resetStateForNewProject();
      } else if (job.id !== this.lastJobId && !this.synchroniseSettings) {
        // Switching jobs within same project with syncing OFF
        console.log("Switching jobs within same project with syncing OFF");
        this._resetStateForNewJob();
      }
    }

    // check if new job
    const isNewJobId = job.id !== this.lastJobId;

    // 2. Set core properties for the NEW job
    this._initializeCoreJobProperties(job);

    // 3. Update last job ID tracker
    this.lastJobId = job.id;

    if (isNewJobId) {
      // check if this new job, has new iterations. If it does, we will give a notification for reparsing
      this.projectService
        .checkForNewIterations(job.id)
        .pipe(take(1))
        .toPromise()
        .then((res) => {
          if ("iterationsAvailable" in res && res["iterationsAvailable"])
            this.notificationService.template(this.newIterationsTemplate as any);
        })
        .catch((err) => {
          console.log(
            "Error when checking for new iterations",
            err.error?.error || "There was an unexpected error",
          );
        });
    }

    // define x and y ranges for the slices
    this.defineRangesForSliceType();
    this.defineCurrentVelocityRange();

    // 4. Restore non-display settings from storage
    this.restoreNonDisplaySettings();

    // 5. Set default IL/XL ranges if they are still null
    this._initializeDefaultRanges();

    // 5. Initialize UI dimensions and settings
    this._initializeSliceDimensions();
    if (job.modelGrid.is2d && this.velocityProfileCrossline == null) {
      this.velocityProfileCrossline = Math.round(job.modelGrid.nx2 / 2);
    }
    this.vpHeight =
      job.modelGrid.is2d || this.vpPositionSwapped
        ? this.height
        : this.depthHeight;
    this.inputHeight = this.height;
    this.inputDepthHeight = this.depthHeight;

    // 6. Perform initial data-dependent calls
    this.getVerticalProfile();
    this.getFunctionalSequence();

    // 7. Return the data fetching observable so we wait for projectData
    return this._fetchAncillaryData(job);
  }

  // --- HELPER METHODS FOR NEW JOB SETUP ---

  private _initializeDefaultRanges(): void {
    const job = this.currentJob;

    if (this.ILXLStartDepth == null) this.ILXLStartDepth = 0;
    if (this.ILXLEndDepth == null) {
      this.ILXLEndDepth = (job.modelGrid.nx3 - 1) * job.modelGrid.dx;
    }
    if (this.ILXLStartInline == null) this.ILXLStartInline = 0;
    if (this.ILXLEndInline == null) {
      this.ILXLEndInline = Math.max(
        1,
        (this.max_inline - 1) * job.modelGrid.dx,
      );
    }
    if (this.ILXLStartCrossline == null) this.ILXLStartCrossline = 0;
    if (this.ILXLEndCrossline == null) {
      this.ILXLEndCrossline = Math.max(
        1,
        (this.max_crossline - 1) * job.modelGrid.dx,
      );
    }
  }

  private _resetStateForNewJob(): void {
    // Slice numbers shouldn't be reset when switching jobs
    // this.inlineNumber = null;
    // this.crosslineNumber = null;
    // this.depthSlice = null;
    this.iteration = null;
    this.timeSlice = null;
    this.shotId = null;

    // Reset model type to default
    this.currentModelTypeId = null;
    this.sliceRanges = {
      inline: null,
      crossline: null,
      depth: null,
    };

    // Reset ranges
    this.displaySettings.min = null;
    this.displaySettings.max = null;
    this.displaySettings.minZ = null;
    this.displaySettings.maxZ = null;
    this.displaySettings.isAuto = true;
    this.defineCurrentVelocityRange();
  }

  private _resetStateForNewProject(): void {
    // Reset slice numbers when switching projects
    this.inlineNumber = null;
    this.crosslineNumber = null;
    this.depthSlice = null;
    this.iteration = null;
    this.timeSlice = null;
    this.shotId = null;

    // Reset model type to default
    this.currentModelTypeId = null;

    // Reset ranges
    this.displaySettings.min = null;
    this.displaySettings.max = null;
    this.displaySettings.minZ = null;
    this.displaySettings.maxZ = null;
    this.displaySettings.isAuto = true;
    this.defineCurrentVelocityRange();

    // Reset overlays
    this.showShotOverlay = false;
    this.currentShotOverlayType = this.shotOverlayTypes[0];

    // Reset horizons
    this.showHorizonTypes = [];
    this.showProjectHorizonTypes = [];
    this.showHorizon = false;

    // Reset IL/XL/depth ranges when switching projects
    this.ILXLStartDepth = null;
    this.ILXLEndDepth = null;
    this.ILXLStartInline = null;
    this.ILXLEndInline = null;
    this.ILXLStartCrossline = null;
    this.ILXLEndCrossline = null;
  }

  private _initializeCoreJobProperties(job: JobDetail): void {
    this.clearModelInfo();
    this.currentJob = job;
    this.comments = job.comments;
    this.waitingFor = job.modelGrid.is2d ? 1 : 3;

    this.max_inline = job.x2_is_inline ? job.modelGrid.nx1 : job.modelGrid.nx2;
    this.max_crossline = job.x2_is_inline
      ? job.modelGrid.nx2
      : job.modelGrid.nx1;

    if (job.modelGrid.is2d) {
      [this.max_inline, this.max_crossline] = [
        this.max_crossline,
        this.max_inline,
      ];
    }

    this.previous_projectID = job.projectId;

    this.ranges = {
      x: [0, job.modelGrid.nx2],
      y: [0, job.modelGrid.nx1],
      z: [0, job.modelGrid.nx3],
    };
  }

  private _fetchAncillaryData(job: JobDetail): Observable<any> {
    // We use forkJoin to run all these independent data fetches in parallel.
    // Each inner observable has its own take(1) and catchError to be resilient.
    // If one API call fails, the others will still complete.

    return forkJoin({
      projectData: this.projectService.getProjectById(job.projectId).pipe(
        take(1),
        catchError(() => of(null)), // Return null if it fails
      ),
      shotOverlayData: forkJoin([
        this.projectService.getShotOverlayTypes(job.id, this.iteration, true),
        this.projectService.getFnlcRanges(job.id),
      ]).pipe(
        take(1),
        catchError(() => of(null)), // Return null if the combined call fails
      ),
      horizonTypes: this.projectService.getHorizonTypes(job.id).pipe(
        take(1),
        catchError(() => of([])), // Return an empty array if it fails
      ),
      costs: this.projectService.getCosts(job.id, job.iterations).pipe(
        take(1),
        catchError(() => of(null)), // Return null if it fails
      ),
    }).pipe(
      tap((results) => {
        // Now we handle the results from all parallel calls in one place.

        // --- 1. Handle Project Data ---
        if (results.projectData) {
          this.projectData = results.projectData;
          this.project_horizons = results.projectData.horizons as any;
        }

        // --- 2. Handle Shot Overlay Data ---
        if (results.shotOverlayData) {
          const [shotTypes, fnlcRanges] = results.shotOverlayData;
          if (shotTypes) {
            let processedShotTypes = [...shotTypes];
            const fnlcType = processedShotTypes.find((t) => t.id === "fnlc");

            if (fnlcRanges && fnlcType) {
              fnlcType.min = fnlcRanges.min_val;
              fnlcType.max = fnlcRanges.max_val;
            } else if (!fnlcRanges && fnlcType) {
              processedShotTypes = processedShotTypes.filter(
                (type) => type.id !== "fnlc",
              );
            }
            this.shotOverlayTypes = processedShotTypes;
          }
        } else {
          this.shotOverlayTypes = null;
        }

        // --- 3. Handle Horizon Types ---
        const horizonData = results.horizonTypes;
        if (horizonData && horizonData.length > 0) {
          this.horizonTypes = horizonData;
          this.showHorizonTypes = [horizonData[0]];
          this.horizonColors = [getRandomHexColor(0)];
        } else {
          this.horizonTypes = [];
          this.showHorizonTypes = [];
          this.horizonColors = [];
          this.showHorizon = false;
        }

        // --- 4. Handle Costs ---
        const costData = results.costs;
        if (
          !costData ||
          !costData.costItems ||
          !costData.costItems.length ||
          costData.type === "simple_shot_geom"
        ) {
          this.epochs = null;
          this.costPerIteration = null;
          this.hasCosts = false;
        } else {
          this.hasCosts = true;
          const batches = costData.totalShots / costData.shotsPerIteration;
          this.epochs = Math.round(
            costData.costItems
              .map((c) => c.iterations)
              .reduce((a, b) => a + b, 0) / costData.costItems.length,
          );
          this.costPerIteration =
            costData.costItems.map((c) => c.cost).reduce((a, b) => a + b, 0) /
            this.epochs /
            batches;
        }

        // --- 5. Call other methods that are not pure data fetching ---
        this.getElapsedTime(); // This can be a simple one-off call
      }),
      takeUntil(this.destroyed$),
    );
  }

  private _initializeSliceDimensions(): void {
    this.getSliceWidths();
    this.getSliceHeights();
    this._triggerRelayout();
  }

  async updateFromQueryParams(queryParams: Params) {
    // 1. Update basic params
    this._updateBasicParams(queryParams);

    // 2. Update model type and processing
    await this._updateModelType(queryParams);

    // 3. Resolve display settings using the manager
    await this._resolveDisplaySettings(queryParams);

    // Ensure auto ranges are fetched before we trigger downstream updates
    this._refreshAutoRanges();

    // 4. Validate and finalize
    this.validateIterations();
    this.updateModelTypeList();
    // this.onSliderChanged();

    this.loadedJob = true;
  }

  private _updateBasicParams(queryParams: Params): void {
    // Iteration - only use from query params if not already set from storage
    // (restoreNonDisplaySettings sets this from storage for the current job)
    if (this.iteration == null && queryParams["iteration"] != null) {
      this.iteration = parseInt(queryParams["iteration"]) || 0;
      if (
        this.currentJob.iterations &&
        this.currentJob.iterations < this.iteration
      ) {
        this.iteration = this.currentJob.iterations;
      }
    }

    // Time slice and shot
    if (queryParams["timeSlice"] != null) {
      this.timeSlice = parseInt(queryParams["timeSlice"]) || 0;
    }
    if (queryParams["shot"] != null) {
      this.shotId = parseInt(queryParams["shot"]) || 0;
    }

    /* Deprecated
    if (queryParams["rules"]) this._rules = queryParams["rules"];
    if (queryParams["searchId"]) this._searchId = queryParams["searchId"];
    if (queryParams["comment"]) this._comment = queryParams["comment"];
    if (queryParams["name"]) this._name = queryParams["name"];
    */
    // Search params
    if (queryParams["jobs"]) this.selectedJobs = queryParams["jobs"];

    // Slice numbers - only use URL params if not already set from storage
    // (restoreNonDisplaySettings sets these from storage for the current job)
    if (
      this.inlineNumber == null &&
      this.isValidSlice(queryParams["inline"], 1)
    ) {
      this.inlineNumber = parseInt(queryParams["inline"]);
    }
    if (
      this.crosslineNumber == null &&
      this.isValidSlice(queryParams["crossline"], 2)
    ) {
      this.crosslineNumber = parseInt(queryParams["crossline"]);
    }
    if (this.depthSlice == null && this.isValidSlice(queryParams["depth"], 3)) {
      this.depthSlice = parseInt(queryParams["depth"]);
    }
  }

  private async _updateModelType(queryParams: Params): Promise<void> {
    const requestedType = queryParams["type"];

    // If model type is already set (restored from storage), don't override with URL
    if (this.currentModelTypeId != null) {
      console.log(
        `Model type already set to ${this.currentModelTypeId}, ignoring URL`,
      );
      return;
    }

    // No URL param or already matches
    if (!requestedType) {
      this.currentModelTypeId = "Vp";
      return;
    }

    if (!requestedType || this.currentModelTypeId === requestedType) {
      // No change or already set
      if (!this.currentModelTypeId) {
        this.currentModelTypeId = "Vp";
      }
      return;
    }

    // Only validate if types are already loaded
    if (this.modelTypes.length > 0 && !this.isValidModelType(requestedType)) {
      console.warn(`Invalid model type: ${requestedType}, defaulting to Vp`);
      this.currentModelTypeId = "Vp";
      return;
    }

    // Store previous type's settings before switching
    if (this.currentModelTypeId) {
      this.persistDisplaySettings();
    }

    this.currentModelTypeId = requestedType;
    this.currentModelTypeProcessing =
      allSliceProcessings[this.currentModelTypeId] || [];

    // Handle special case: One-Way Traveltime
    if (this.currentModelTypeId === "One-Way Traveltime") {
      this.showDepthSliceGraph = false;
      this.toggleDepthSlice({ checked: false });
    }
  }

  private async _resolveDisplaySettings(queryParams: Params): Promise<void> {
    // Skip resolution if synchronise settings is on and we're switching jobs
    if (
      this.synchroniseSettings &&
      this.previousJob &&
      this.currentJob.id !== this.previousJob.id
    ) {
      console.log(
        "Skipping display settings resolution due to synchroniseSettings",
      );
      return;
    }

    // Use the settings manager to resolve settings
    const resolvedSettings = await this.settingsManager.resolveSettings(
      queryParams,
      this.currentModelTypeId,
      this.currentJob.id,
      this.projectData,
      this.currentModelTypeProcessing,
    );

    this.displaySettings = resolvedSettings;
    this.defineCurrentVelocityRange();
    this.currentJob.last_used_palette = resolvedSettings.palette;

    /// In auto mode, ranges will be applied as slices load and emit their trueMin/trueMax
    // via onColorbarTextEmit() -> _applyAutoRangesFromSliceData()
  }

  updateModelTypeList() {
    let modelTypes$: Observable<ModelType[] | [ModelType[], ModelType[]]>;

    if (this.iteration !== 0) {
      modelTypes$ = forkJoin([
        this.projectService.getModelTypes(this.currentJob.id, this.iteration),
        this.projectService.getModelTypes(this.currentJob.id, 0),
      ]);
    } else {
      modelTypes$ = this.projectService.getModelTypes(
        this.currentJob.id,
        this.iteration,
      );
    }

    modelTypes$
      .pipe(take(1)) // take(1) is fine here as it's a one-off call
      .subscribe({
        next: (data) => this._handleModelTypesResult(data),
        error: (err) => this._handleModelTypesError(err),
      });
  }

  private _handleModelTypesResult(
    data: ModelType[] | [ModelType[], ModelType[]],
  ): void {
    let modelTypesData: ModelType[];

    if (Array.isArray(data[0])) {
      // This is the forkJoin result: [dataForIter, dataForIterZero]
      modelTypesData = data[0] as ModelType[];
      const iterZeroData = data[1] as ModelType[];
      if (
        iterZeroData.some((d) => d.id === "Delta") &&
        !modelTypesData.some((m) => m.id === "Delta")
      ) {
        modelTypesData.push({ id: "Delta", name: "Delta" });
      }
      if (
        iterZeroData.some((d) => d.id === "Epsilon") &&
        !modelTypesData.some((m) => m.id === "Epsilon")
      ) {
        modelTypesData.push({ id: "Epsilon", name: "Epsilon" });
      }
    } else {
      // This is the single API call result
      modelTypesData = data as ModelType[];
      if (this.currentJob.wellLogs?.length) {
        modelTypesData = modelTypesData.concat([
          { id: "Gradient", name: "Gradient" },
          { id: "Total_Vp_Update", name: "Total_Vp_Update" },
        ]);
      }
    }

    this.modelTypes = modelTypesData;
    if (
      !this.modelTypes.some(
        (modelType) => modelType.id === this.currentModelTypeId,
      )
    ) {
      this.notificationService.warning(
        "Model Property Not Available",
        `The model property "${this.currentModelTypeId}" is not available for this job.`,
      );
    }
  }

  private _handleModelTypesError(err: any): void {
    this.modelTypes = [{ id: "Vp", name: "Vp" }];
    if (this.currentModelTypeId !== "Vp") {
      this.notificationService.warning(
        "Model Property Not Available",
        `Could not fetch model properties.`,
      );
    }
  }

  getElapsedTime() {
    if (this.currentJob.runTime == null || !this.currentJob.startTime) {
      return;
    }

    var runTime =
      this.currentJob.runTime +
      Math.floor(
        Math.abs(this.currentJob.startTime.getTime() - new Date().getTime()) /
          1000,
      );

    if (runTime < 60) this.elapsedTime = runTime + "s";
    else if (runTime < 60 * 60)
      this.elapsedTime = Math.floor(runTime / 60) + "m " + (runTime % 60) + "s";
    else {
      var hours = Math.floor(runTime / (60 * 60));
      var minutes = Math.floor(runTime / 60) - hours * 60;
      this.elapsedTime = `${hours}h ${minutes}m`;
    }
  }

  getWellName(sonicId) {
    let wellLog = this.currentJob.wellLogs.find((l) => l.id == sonicId);
    if (!wellLog || !this.showSonicLog) return "";
    return wellLog.name;
  }

  /**
   * return true or false dependent on whether the slice is valid or not.
   * takes slice number and slice index [crossline, inline, depth] as input to verify
   */
  isValidSlice(num: number, index: number): boolean {
    if (!num) return false;
    if (num < 0) return false;

    if (!this.currentJob) return true;

    let val: number;
    if (index === 1) val = this.currentJob.modelGrid.nx1;
    else if (index === 2) val = this.currentJob.modelGrid.nx2;
    else val = this.currentJob.modelGrid.nx3;

    if (num > val) return false;

    return true;
  }

  isValidModelType(type: string): boolean {
    if (!type) return false;

    return !!this.modelTypes.find((m) => m.id == type);
  }

  onSonicLogClicked() {
    this.depthSliceWellLogs = [];
    this.showWellLog = false;
    this.sonicLogId = this.showShowLogId();

    this._forceDataReload();
  }

  onColorChanged(color) {
    this.onSonicLogClicked();
  }

  setLog(id) {
    var log = this.currentJob.wellLogs.find((d) => d.id == id);
    if (!log) return;

    this.selectedSonicId = id;
    this.inlineNumber = log.inline;
    this.crosslineNumber = log.crossline;
    this.showSonicLog = true;
    this.sonicLogId = this.showShowLogId();
    this.onSonicLogClicked();
    this.onSliderChanged();
  }

  setProjectHorizon(id) {
    var type = this.showProjectHorizonTypes.find((d) => d == id);
    if (type) {
      let i = this.showProjectHorizonTypes.indexOf(type);
      this.showProjectHorizonTypes.splice(i, 1);
      this.projectHorizonColors.splice(i, 1);
    } else {
      let i = this.showProjectHorizonTypes.indexOf(type);
      this.showHorizon = true;
      this.showProjectHorizonTypes.push(id);
      this.projectHorizonColors.push(
        getRandomHexColor(this.projectHorizonColors.length),
      );
    }
    this.onSonicLogClicked();
  }

  setHorizon(id) {
    var type = this.showHorizonTypes.find((d) => d == id);
    if (type) {
      let i = this.showHorizonTypes.indexOf(type);
      this.showHorizonTypes.splice(i, 1);
      this.horizonColors.splice(i, 1);
    } else {
      let i = this.showHorizonTypes.indexOf(type);
      this.showHorizon = true;
      this.showHorizonTypes.push(id);
      this.horizonColors.push(
        getRandomHexColor(this.showHorizonTypes.length - 1),
      );
    }
    this.onSonicLogClicked();
  }

  isHorizonSelected(id) {
    return this.showHorizonTypes.find((d) => d == id);
  }

  isProjectHorizonSelected(id) {
    return this.showProjectHorizonTypes.find((d) => d == id);
  }

  onSliderChanged(iteration?) {
    if (iteration != null && iteration > this.currentJob.iterationsComplete) {
      return;
    }

    const newParams = this._buildQueryParams(iteration);
    this.getAvailableTimeSlices(this.currentModelTypeId);
    // Wait for navigation to complete before triggering update
    this.router
      .navigate(["."], {
        relativeTo: this.route,
        queryParams: newParams,
        replaceUrl: true,
      })
      .then(() => {
        // Navigation complete, now force chart update
        this._forceDataReload();
        this.getVerticalProfile();
      });
  }

  private _buildQueryParams(iteration?: number): any {
    // Sanitize NaN values
    const sanitize = (val: any) => {
      if (val == null || (typeof val === "number" && isNaN(val))) {
        return null;
      }
      return val;
    };

    return {
      iteration: iteration != null ? iteration : this.iteration,
      inline: this.inlineNumber,
      crossline: this.crosslineNumber,
      depth: this.depthSlice,
      type: this.currentModelTypeId,
      // Only include ranges if not in auto mode
      ...(this.displaySettings.isAuto
        ? {}
        : {
            minVal: sanitize(this.displaySettings.min),
            maxVal: sanitize(this.displaySettings.max),
            minValZ: sanitize(this.displaySettings.minZ),
            maxValZ: sanitize(this.displaySettings.maxZ),
          }),
      jobs: this.selectedJobs,
      rules: this._rules,
      searchId: this._searchId,
      comment: this._comment,
      name: this._name,
      timeSlice: this.timeSlice,
      shot: this.shotId,
    };
  }

  onSynchroniseChange(value) {
    this.storedSettingsService.setSynchroniseSettings(value);
    this.storeSettings();
    this.persistDisplaySettings();
  }

  // Wrapper for keyboard event
  onKeySubmission(event: KeyboardEvent) {
    if (event.key == "Enter" && this.vpUpdateCheck) {
      this.onSonicLogClicked();
    }
  }

  onKeySubmissionShotOverlayType(event: KeyboardEvent) {
    if (event.key == "Enter") {
      let min = this.currentShotOverlayType.min;
      let max = this.currentShotOverlayType.max;
      if (min == null || isNaN(min)) {
        min = 0;
      }
      if (max == null || isNaN(max)) {
        max = 100;
      }
      this.currentShotOverlayType.min = min;
      this.currentShotOverlayType.max = max;
      this.currentShotOverlayType = Object.assign(
        {},
        this.currentShotOverlayType,
        {},
      ); // updates reference
    }
  }
  resetRanges() {
    this.ILXLStartInline = 0;
    this.ILXLStartCrossline = 0;
    this.ILXLStartDepth = 0;
    this.ILXLEndInline = Math.max(
      1,
      (this.max_inline - 1) * this.currentJob.modelGrid.dx,
    );
    this.ILXLEndCrossline = Math.max(
      1,
      (this.max_crossline - 1) * this.currentJob.modelGrid.dx,
    );
    this.ILXLEndDepth =
      (this.currentJob.modelGrid.nx3 - 1) * this.currentJob.modelGrid.dx;
    this.onSonicLogClicked();
  }

  submitSliceRanges(event: KeyboardEvent) {
    if (event.key == "Enter") {
      this.onKeySubmissionDepthRange();
      this.onKeySubmissionCrosslineRange();
      this.onKeySubmissionInlineRange();
      this.onSonicLogClicked();
      this.storeSettings();
    }
  }
  onKeySubmissionDepthRange() {
    if (!this.ILXLStartDepth || this.ILXLStartDepth < 0) {
      this.ILXLStartDepth = 0;
    }
    if (
      this.ILXLStartDepth >
      (this.currentJob.modelGrid.nx3 - 1) * this.currentJob.modelGrid.dx - 500
    ) {
      this.ILXLStartDepth =
        (this.currentJob.modelGrid.nx3 - 1) * this.currentJob.modelGrid.dx -
        500;
    }
    if (
      !this.ILXLEndDepth ||
      this.ILXLEndDepth >
        (this.currentJob.modelGrid.nx3 - 1) * this.currentJob.modelGrid.dx
    ) {
      this.ILXLEndDepth =
        (this.currentJob.modelGrid.nx3 - 1) * this.currentJob.modelGrid.dx;
    }
    if (this.ILXLEndDepth < this.ILXLStartDepth) {
      this.ILXLEndDepth = this.ILXLStartDepth + 500;
    }
  }

  onKeySubmissionInlineRange() {
    if (this.ILXLStartInline == null || this.ILXLStartInline < 0) {
      this.ILXLStartInline = 0;
    }
    if (
      this.ILXLEndInline == null ||
      this.ILXLEndInline > (this.max_inline - 1) * this.currentJob.modelGrid.dx
    ) {
      this.ILXLEndInline = (this.max_inline - 1) * this.currentJob.modelGrid.dx;
    }
    if (this.ILXLEndInline < this.ILXLStartInline) {
      this.ILXLEndInline = Math.min(
        (this.max_inline - 1) * this.currentJob.modelGrid.dx,
        this.ILXLStartInline + 500,
      );
    }
    this.ILXLEndInline = Math.max(1, this.ILXLEndInline);
  }

  onKeySubmissionCrosslineRange() {
    if (this.ILXLStartCrossline == null || this.ILXLStartCrossline < 0) {
      this.ILXLStartCrossline = 0;
    }
    if (
      this.ILXLEndCrossline == null ||
      this.ILXLEndCrossline >
        (this.max_crossline - 1) * this.currentJob.modelGrid.dx
    ) {
      this.ILXLEndCrossline =
        (this.max_crossline - 1) * this.currentJob.modelGrid.dx;
    }
    if (this.ILXLEndCrossline < this.ILXLStartCrossline) {
      this.ILXLEndCrossline = Math.min(
        (this.max_crossline - 1) * this.currentJob.modelGrid.dx,
        this.ILXLStartCrossline + 500,
      );
    }
    this.ILXLEndCrossline = Math.max(1, this.ILXLEndCrossline);
  }

  showShowLogId() {
    if (
      this.selectedSonicId &&
      this.selectedSonicId.inline == this.inlineNumber &&
      this.selectedSonicId.crossline == this.crosslineNumber
    ) {
      return this.selectedSonicId;
    }
    if (
      !this.currentJob.wellLogs ||
      !this.showSonicLog ||
      this.currentJob.wellLogs.length == 0
    )
      return null;
    let log = this.currentJob.wellLogs.find(
      (l) => l.id == this.selectedSonicId,
    );
    if (
      log &&
      log.inline == this.inlineNumber &&
      log.crossline == this.crosslineNumber
    ) {
      return this.selectedSonicId;
    }
    this.showSonicLog = false;
    this.selectedSonicId = null;
    return false;
  }

  onShowWellLog() {
    if (this.showWellLog) {
      if (this.currentJob.x2_is_inline) {
        this.depthSliceWellLogs = this.currentJob.wellLogs.map((well) => ({
          x: well.inline * this.currentJob.modelGrid.dx,
          y: well.crossline * this.currentJob.modelGrid.dx,
        }));
      } else {
        this.depthSliceWellLogs = this.currentJob.wellLogs.map((well) => ({
          x: well.crossline * this.currentJob.modelGrid.dx,
          y: well.inline * this.currentJob.modelGrid.dx,
        }));
      }
    } else {
      this.depthSliceWellLogs = [];
    }
  }

  getSonicNXForSliceType(type: SliceType) {
    let log = this.currentJob.wellLogs.find(
      (l) => l.id == this.selectedSonicId,
    );
    if (!log) return null;

    switch (type) {
      case SliceType.inline: {
        return log.crossline;
      }
      case SliceType.crossLine: {
        return log.inline;
      }
      case SliceType.depth: {
        let locs = this.currentJob.wellLogs.map((w) => [
          w.crossline,
          w.inline,
          w.id == log.id,
        ]);
        return locs;
      }
    }
    return null;
  }

  getSonicWidthForSliceType(type: SliceType) {
    switch (type) {
      case SliceType.inline:
        return 60;
      case SliceType.crossLine:
        return (
          60 *
          (this.currentJob.modelGrid.nx1 / this.currentJob.modelGrid.nx2 < 0.2
            ? 0.8
            : 1)
        );
    }
  }

  getWidthForSliceType(type: SliceType) {
    const containerWidth = document
      .getElementsByClassName("chart-slice-container")[0]
      .getBoundingClientRect().width;

    var inline = 140 + ((containerWidth - 400) * this.inlineWidth) / 100;
    var crossline = $("#slice-settings").width() - inline - 10;

    switch (type) {
      case SliceType.inline:
        return this.inlineWidth !== 100
          ? this.sanitizer.bypassSecurityTrustStyle(inline + "px")
          : "100%";
      case SliceType.crossLine:
        return this.crosslineWidth !== 100
          ? this.sanitizer.bypassSecurityTrustStyle(crossline + "px")
          : "100%";
      case SliceType.depth:
        return this.inlineWidth !== 100
          ? this.sanitizer.bypassSecurityTrustStyle(inline + "px")
          : "100%";
      case null: // for velocity profile
        let is2d = this.currentJob.modelGrid.is2d;
        return this.crosslineWidth !== 100
          ? this.sanitizer.bypassSecurityTrustStyle(crossline / 2 + "px")
          : is2d
            ? "calc(25% - 30px)"
            : "calc(50% - 30px)";
    }
  }

  getSliceWidths() {
    if (
      window.innerWidth < 992 ||
      !this.currentJob ||
      this.currentJob.modelGrid.is2d
    ) {
      this.inlineWidth = 100;
      this.crosslineWidth = 100;
      return;
    }

    // If full width IL/XL is enabled, make both inline and crossline take full width
    if (this.fullWidthILXL) {
      this.inlineWidth = 100;
      this.crosslineWidth = 100;
      return;
    }

    var inline = Math.round(
      (100 * this.currentJob.modelGrid.nx2) /
        (this.currentJob.modelGrid.nx1 + this.currentJob.modelGrid.nx2),
    );
    if (this.currentJob.modelGrid.nx1 / this.currentJob.modelGrid.nx2 < 0.2) {
      inline = inline * 0.97;
      //if (this.showSonicLog) {
      //  inline = inline * 0.95;
      //}
    }

    this.inlineWidth = inline;
    this.crosslineWidth = 100 - inline;
  }

  getShotOverlay(type: SliceType) {
    if (type == SliceType.depth) return this.showShotOverlay;

    return false;
  }

  getProjectHorizons(type: SliceType) {
    if (this.showHorizon && this.showProjectHorizonTypes.length > 0) {
      return this.showProjectHorizonTypes;
    }
    return null;
  }

  getProjectHorizonName(id: string) {
    return this.project_horizons.find((h) => h.id == id).name;
  }
  getHorizons(type: SliceType) {
    if (
      this.showHorizon &&
      this.horizonTypes &&
      this.showHorizonTypes.length > 0
    ) {
      return this.showHorizonTypes;
    }
    return null;
  }

  getTimeSliceInterval() {
    if (!this.availTimeSlices || this.availTimeSlices.length <= 1) {
      return 0;
    }

    return this.availTimeSlices[1] - this.availTimeSlices[0];
  }

  @HostListener("window:resize", ["$event"])
  onResize(event) {
    this.getSliceWidths();
    this.getSliceHeights();
  }

  @HostListener("window:beforeunload", ["$event"])
  onbeforeunload(event) {
    this.storeSettings();
    this.persistDisplaySettings();
  }

  setShotOverlayType(shotOverlayType: ShotOverlayType) {
    this.currentShotOverlayType = shotOverlayType;
    this.showShotOverlay = true;
  }

  setModelType(modelType: ModelType, event) {
    if (event != null) {
      event.stopPropagation();
    }
    if (this.currentModelTypeId == modelType.id) return;

    // store current type settings before switching
    this.persistDisplaySettings();

    // Update to new type
    this.currentModelTypeId = modelType.id;
    this.currentModelTypeProcessing = modelType.processing || [];

    // Clear displaySettings and slice ranges so new type's settings are resolved from storage/defaults
    // This prevents old type's ranges from leaking into URL
    this.displaySettings = {
      min: null,
      max: null,
      minZ: null,
      maxZ: null,
      isAuto: true, // Default to auto, will be resolved by settings manager
      isAutoSynced: true,
      palette: null,
    };
    this.defineCurrentVelocityRange();

    this.sliceRanges = {
      inline: null,
      crossline: null,
      depth: null,
    };

    // Let onSliderChanged trigger the URL update, which will then
    // call updateFromQueryParams and resolve the new type's settings
    this.onSliderChanged();
  }

  setPalette(palette: string) {
    this.currentJob.last_used_palette = palette;
    this.displaySettings.palette = palette;
    this.persistDisplaySettings();
  }

  setTimeSlice(timeSlice) {
    this.timeSlice = timeSlice;
    this.onSliderChanged();
  }

  setShot(shot) {
    this.shotId = shot;
    this.onSliderChanged();
  }

  getSliceHeights() {
    if (this.maxDepth != 0) return;

    if (
      window.innerWidth < 992 ||
      !this.currentJob ||
      this.currentJob.modelGrid.is2d
    ) {
      if (!this.currentJob) {
        this.depthHeight = 250;
        return;
      }

      if (this.depthHeight != null) return;
      this.depthHeight = Math.round(
        80 +
          (this.currentJob.modelGrid.nx1 / this.currentJob.modelGrid.nx2) *
            (window.innerWidth - 320),
      );
      if (this.depthHeight < 200) this.depthHeight = 200;
      return;
    }
    var sliceWidth = $("#slice-settings").width();
    if (!sliceWidth) {
      sliceWidth = window.innerWidth - 180;
    }
    var inline = Math.round(
      110 + ((window.innerWidth - 400) * this.inlineWidth) / 100,
    );
    var crossline = sliceWidth - inline + 30;

    if (this.depthHeight == null) this.depthHeight = crossline - 100;
    if (this.depthHeight < 200) this.depthHeight = 200;

    this.maxDepth = Math.max(650, this.depthHeight);
  }

  getAdjustedSliceType = (type: SliceType) => {
    if (!this.currentJob || this.currentJob.modelGrid.is2d) return type;

    switch (type) {
      case SliceType.inline:
        return this.currentJob.x2_is_inline
          ? SliceType.inline
          : SliceType.crossLine;
      case SliceType.crossLine:
        return this.currentJob.x2_is_inline
          ? SliceType.crossLine
          : SliceType.inline;
      case SliceType.depth:
        return SliceType.depth;
    }

    return type;
  };

  getTitleForSliceType = (type: SliceType) => {
    switch (type) {
      case SliceType.inline:
        return "Inline " + (!!this.inlineNumber ? this.inlineNumber : "");
      case SliceType.crossLine:
        return "Crossline " + this.crosslineNumber;
      case SliceType.depth:
        return "Depth " + this.depthSlice;
    }
    return "";
  };

  getHoverLabelX = (type: SliceType) => {
    switch (type) {
      case SliceType.inline:
        return "XL";
      case SliceType.crossLine:
        return "IL";
      case SliceType.depth:
        return "XL";
    }
  };

  getHoverLabelY = (type: SliceType) => {
    switch (type) {
      case SliceType.inline:
        return "Depth";
      case SliceType.crossLine:
        return "Depth";
      case SliceType.depth:
        return "IL";
    }
  }

  defineRangesForSliceType() {
    let temp = {};
    temp[`${SliceType.inline}`] = this.getRangesForSliceType(SliceType.inline);
    temp[`${SliceType.crossLine}`] = this.getRangesForSliceType(
      SliceType.crossLine,
    );
    temp[`${SliceType.depth}`] = this.getRangesForSliceType(SliceType.depth);

    this.i_sliceRanges = temp;
  }

  getRangesForSliceType(type: SliceType): Ranges {
    switch (type) {
      case SliceType.inline:
        return {
          x: [0, this.currentJob.modelGrid.nx2 * this.currentJob.modelGrid.dx],
          y: [this.currentJob.modelGrid.nx3 * this.currentJob.modelGrid.dx, 0],
        };
      case SliceType.crossLine:
        return {
          x: [0, this.currentJob.modelGrid.nx1 * this.currentJob.modelGrid.dx],
          y: [this.currentJob.modelGrid.nx3 * this.currentJob.modelGrid.dx, 0],
        };
      case SliceType.depth:
        return {
          x: [0, this.currentJob.modelGrid.nx2 * this.currentJob.modelGrid.dx],
          y: [0, this.currentJob.modelGrid.nx1 * this.currentJob.modelGrid.dx],
        };
    }
    return null;
  }

  getXStepForSliceType = (type: SliceType): number => {
    switch (type) {
      case SliceType.inline:
        return this.currentJob.modelGrid.nx3;
      case SliceType.crossLine:
        return this.currentJob.modelGrid.nx3;
      case SliceType.depth:
        return this.currentJob.modelGrid.nx1;
    }
  };

  getYStepForSliceType = (type: SliceType): number => {
    switch (type) {
      case SliceType.inline:
        return this.currentJob.modelGrid.nx2;
      case SliceType.crossLine:
        return this.currentJob.modelGrid.nx1;
      case SliceType.depth:
        return this.currentJob.modelGrid.nx2;
    }
  };

  getNumberForSliceType = (type: SliceType): number => {
    switch (type) {
      case SliceType.inline:
        return this.inlineNumber;
      case SliceType.crossLine:
        return this.crosslineNumber;
      case SliceType.depth:
        return this.depthSlice;
    }
    return 0;
  };

  getUnit(): string {
    switch (this.currentJob.modelGrid.unit) {
      case "M":
        return "km";
      case "I":
        return "ft";
    }
    return "";
  }

  getXTitleForSliceType = (type: SliceType): string => {
    switch (type) {
      case SliceType.inline:
        return "crossline (" + this.getUnit() + ")";
      case SliceType.crossLine:
        return "inline (" + this.getUnit() + ")";
      case SliceType.depth:
        return "crossline (" + this.getUnit() + ")";
    }
    return "";
  };

  getYTitleForSliceType = (type: SliceType): string => {
    switch (type) {
      case SliceType.inline:
        return "depth (" + this.getUnit() + ")";
      case SliceType.crossLine:
        return "depth (" + this.getUnit() + ")";
      case SliceType.depth:
        return "inline (" + this.getUnit() + ")";
    }
    return "";
  };

  getXForSliceType = (type: SliceType): number => {
    if (this.currentJob.modelGrid.is2d) {
      // return null;
      return this.velocityProfileCrossline * this.currentJob.modelGrid.dx;
    }

    switch (type) {
      case SliceType.inline:
        return (
          this.crosslineNumber * this.currentJob.modelGrid.dx -
          this.currentJob.modelGrid.dx / 2
        );
      case SliceType.crossLine:
        return (
          this.inlineNumber * this.currentJob.modelGrid.dx -
          this.currentJob.modelGrid.dx / 2
        );
      case SliceType.depth:
        return (
          this.crosslineNumber * this.currentJob.modelGrid.dx -
          this.currentJob.modelGrid.dx / 2
        );
    }
    return null;
  };

  // slice numbers begin from 1
  getYForSliceType = (type: SliceType): number => {
    if (this.currentJob.modelGrid.is2d) {
      // return null;
      return (
        this.depthSlice * this.currentJob.modelGrid.dx -
        this.currentJob.modelGrid.dx / 2
      );
    }

    switch (type) {
      case SliceType.inline:
        return (
          this.depthSlice * this.currentJob.modelGrid.dx -
          this.currentJob.modelGrid.dx / 2
        );
      case SliceType.crossLine:
        return (
          this.depthSlice * this.currentJob.modelGrid.dx -
          this.currentJob.modelGrid.dx / 2
        );
      case SliceType.depth:
        return (
          this.inlineNumber * this.currentJob.modelGrid.dx -
          this.currentJob.modelGrid.dx / 2
        );
    }
    return null;
  };

  getCurrentCost(): number {
    return 20 * this.iteration;
  }

  validateIterations() {
    if (!this.currentJob) return;

    if (!this.currentJob.modelGrid.is2d) {
      if (!this.isValidSlice(this.inlineNumber, 1)) {
        this.inlineNumber = this.currentJob.defaultNx1;
        console.warn(
          `INLINE NUMBER WAS NOT VALID, SET TO ${this.currentJob.defaultNx1} as default`,
        );
      }

      if (!this.isValidSlice(this.crosslineNumber, 2)) {
        this.crosslineNumber = this.currentJob.defaultNx2;
        console.warn(
          `CROSSLINE NUMBER WAS NOT VALID, SET TO ${this.currentJob.defaultNx2} as default`,
        );
      }

      if (!this.isValidSlice(this.depthSlice, 3)) {
        this.depthSlice = this.currentJob.defaultNx3;
        console.warn(
          `DEPTH NUMBER WAS NOT VALID, SET TO ${this.currentJob.defaultNx3} as default`,
        );
      }
    }

    if (!this.iteration && this.iteration != 0) {
      this.iteration = 0;
    }
    if (this.iteration < 0) this.iteration = 0;
    else if (this.iteration > this.currentJob.iterationsComplete)
      this.iteration = this.currentJob.iterationsComplete;
  }

  expandChart(slice: Slice) {
    this.isLoading = true;
    let array = this.slices.filter((t) => t.sliceType !== slice.sliceType);
    array.unshift(slice);
    this.slices = array;

    setTimeout(() => {
      this._triggerRelayout();

      setTimeout(() => {
        this.isLoading = false;
      }, 200);
    }, 500);
  }

  private _forceDataReload(delay: number = 200): void {
    this.zone.run(() => {
      this.reloadChartData = true;
      setTimeout(() => {
        this.reloadChartData = false;
      }, delay);
    });
  }

  private _triggerRelayout(): void {
    this.relayoutChart = !this.relayoutChart;
  }

  defineCurrentVelocityRange() {
    let temp = {};
    temp[`${SliceType.inline}`] = this.getCurrentVelocityRange(
      SliceType.inline,
    );
    temp[`${SliceType.crossLine}`] = this.getCurrentVelocityRange(
      SliceType.crossLine,
    );
    temp[`${SliceType.depth}`] = this.getCurrentVelocityRange(SliceType.depth);

    this.i_sliceVelocityRanges = temp;
  }
  getCurrentVelocityRange(type: SliceType): number[] {
    if (type === SliceType.depth) {
      return [this.displaySettings.minZ, this.displaySettings.maxZ];
    }

    if (this.displaySettings.isAuto) {
      return this.displaySettings.isAutoSynced
        ? [this.displaySettings.min, this.displaySettings.max]
        : [null, null];
    }

    return [this.displaySettings.min, this.displaySettings.max];
  }

  showMinMaxDialog(type: SliceType) {
    const { min: currentMin, max: currentMax } =
      this._getDialogRangeForSlice(type);
    const isInlineOrCrossline =
      type === SliceType.inline || type === SliceType.crossLine;

    const dialogRef = this.dialog.open(VelocityComponent, {
      width: "400px",
      data: {
        singleValue: false,
        min: currentMin,
        max: currentMax,
        auto: this.displaySettings.isAuto,
        autoSynced: this.displaySettings.isAutoSynced,
        slice_processing: this.currentModelTypeProcessing,
        isDepth: type === SliceType.depth,
      },
    });

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntil(this.destroyed$))
      .subscribe((result: MinMaxDialog) => {
        if (!result) return;

        if (result.auto) {
          this.displaySettings.isAuto = true;
          if (isInlineOrCrossline) {
            this.displaySettings.isAutoSynced = result.autoSynced ?? true;
            this.displaySettings.min = null;
            this.displaySettings.max = null;
          } else {
            this.displaySettings.minZ = null;
            this.displaySettings.maxZ = null;
          }
          this.defineCurrentVelocityRange();
          this._refreshAutoRanges(true);
          this.persistDisplaySettings(true);
          this.onSliderChanged();
          return;
        }

        // Manual mode
        this.displaySettings.isAuto = false;
        if (this.currentModelTypeId === "Vp") {
          result.min = Math.max(result.min, vpLowerBound);
          result.min = Math.min(result.min, vpUpperBound);
          result.max = Math.max(result.max, vpLowerBound);
          result.max = Math.min(result.max, vpUpperBound);
        }

        if (type === SliceType.depth) {
          this.displaySettings.minZ = result.min;
          this.displaySettings.maxZ = result.max;
        } else {
          this.displaySettings.min = result.min;
          this.displaySettings.max = result.max;
        }
        this.defineCurrentVelocityRange();
        this.persistDisplaySettings(true);
        this.onSliderChanged();
      });
  }

  private restoreNonDisplaySettings() {
    // This method now only handles non-display settings
    // Display settings are handled by updateFromQueryParams via settingsManager

    const modelViewSettings =
      this.storedSettingsService.getSettings<ModelViewSettings>(
        this.settingsKey,
        this.currentJob.projectId,
        SettingTypes.ModelView,
      );

    // Restore UI dimensions
    if (modelViewSettings?.height) {
      this.height = modelViewSettings.height;
    }
    if (modelViewSettings?.depthHeight) {
      this.depthHeight = modelViewSettings.depthHeight;
    }

    // Restore IL/XL ranges (only if not already set)
    this._restoreILXLRanges(modelViewSettings);

    // Restore depth slice visibility
    this.resetDepthSliceVisiblity(modelViewSettings?.showDepthSlice);

    // Restore model-specific settings
    const settings = this.storedSettingsService.getSettings<ModelSettings>(
      this.settingsKey,
      this.currentJob.id,
      SettingTypes.Model,
    );

    if (!settings) {
      this.iteration = 0;
      if (!this.synchroniseSettings) {
        this.vpUpdateCheck = false;
        this.vpDiffIteration = null;
      }
      return;
    }

    // Restore model settings (but NOT display settings)
    this.iteration = settings.iteration;
    this.showHorizonTypes = settings.horizonTypes;
    this.showHorizon = settings.showHorizon;
    this.vpUpdateCheck = settings.vpUpdateCheck;
    this.vpUpdateBlockCheck = settings.vpUpdateBlockCheck;
    this.vpDiffIteration = settings.vpDiffIteration;
    this.fullWidthILXL = settings.fullWidthILXL || false;

    // Restore model type only if not already set
    if (this.currentModelTypeId == null && settings.modelTypeId) {
      this.currentModelTypeId = settings.modelTypeId;
      this.currentModelTypeProcessing =
        allSliceProcessings[this.currentModelTypeId] || [];
      console.log(`Restored model type to ${this.currentModelTypeId}`);
    }

    // Restore slice numbers only if not already set
    if (this.inlineNumber == null) {
      this.inlineNumber = settings.inlineNumber;
    }
    if (this.crosslineNumber == null) {
      this.crosslineNumber = settings.crosslineNumber;
    }
    if (this.depthSlice == null) {
      this.depthSlice = settings.depthSlice;
    }

    // Restore vertical profile axis
    if (!this.syncAxis) {
      this.zone.run(() => {
        this.verticalProfileAxis =
          settings.verticalProfileAxis == null ||
          (settings.verticalProfileAxis[0] == null &&
            settings.verticalProfileAxis[1] == null)
            ? [null, null]
            : settings.verticalProfileAxis;
      });
    }

    this.restoredScrollPos = settings.scrollPos;

    // NOTE: currentModelTypeId will be set from URL params
    // Display settings will be resolved by settingsManager in updateFromQueryParams
  }

  private _restoreILXLRanges(modelViewSettings: ModelViewSettings): void {
    if (!modelViewSettings) return;

    const job = this.currentJob;

    // Helper to restore value only if current is null/default
    const restoreIfDefault = (
      current: number | null,
      stored: number | null,
      defaultVal: number,
      maxVal: number,
    ): number | null => {
      if (current == null || current === defaultVal) {
        return stored != null ? Math.min(stored, maxVal) : null;
      }
      return current;
    };

    if (modelViewSettings.ILXLStartDepth != null) {
      this.ILXLStartDepth = restoreIfDefault(
        this.ILXLStartDepth,
        modelViewSettings.ILXLStartDepth,
        0,
        job.modelGrid.nx3 * job.modelGrid.dx,
      );
    }

    if (modelViewSettings.ILXLEndDepth != null) {
      this.ILXLEndDepth = restoreIfDefault(
        this.ILXLEndDepth,
        modelViewSettings.ILXLEndDepth,
        job.modelGrid.nx3 * job.modelGrid.dx,
        job.modelGrid.nx3 * job.modelGrid.dx,
      );
    }

    if (modelViewSettings.ILXLStartInline != null) {
      this.ILXLStartInline = restoreIfDefault(
        this.ILXLStartInline,
        modelViewSettings.ILXLStartInline,
        0,
        job.modelGrid.nx1 * job.modelGrid.dx,
      );
    }

    if (modelViewSettings.ILXLEndInline != null) {
      const num_inline = job.x2_is_inline
        ? job.modelGrid.nx1
        : job.modelGrid.nx2;

      this.ILXLEndInline = restoreIfDefault(
        this.ILXLEndInline,
        modelViewSettings.ILXLEndInline,
        num_inline * job.modelGrid.dx,
        num_inline * job.modelGrid.dx,
      );
    }

    if (modelViewSettings.ILXLStartCrossline != null) {
      this.ILXLStartCrossline = restoreIfDefault(
        this.ILXLStartCrossline,
        modelViewSettings.ILXLStartCrossline,
        0,
        job.modelGrid.nx2 * job.modelGrid.dx,
      );
    }

    if (modelViewSettings.ILXLEndCrossline != null) {
      const num_crossline =
        job.x2_is_inline || job.modelGrid.is2d
          ? job.modelGrid.nx2
          : job.modelGrid.nx1;

      this.ILXLEndCrossline = restoreIfDefault(
        this.ILXLEndCrossline,
        modelViewSettings.ILXLEndCrossline,
        num_crossline * job.modelGrid.dx,
        num_crossline * job.modelGrid.dx,
      );
    }
  }

  private storeSettings() {
    let currentSettings: ModelSettings = {
      modelTypeId: this.currentModelTypeId,
      horizonTypes: this.showHorizonTypes,
      id: this.currentJob.id,
      projectId: this.currentJob.projectId,
      iteration: this.iteration,
      showHorizon: this.showHorizon,
      inlineNumber: this.inlineNumber,
      crosslineNumber: this.crosslineNumber,
      depthSlice: this.depthSlice,
      verticalProfileAxis: this.verticalProfileAxis,
      scrollPos: this.scrollPos,
      vpUpdateCheck: this.vpUpdateCheck,
      vpUpdateBlockCheck: this.vpUpdateBlockCheck,
      vpDiffIteration: this.vpDiffIteration,
      fullWidthILXL: this.fullWidthILXL,
      // syncRange: this.syncRangeBetweenSlices
    };
    this.storedSettingsService.setSettings<ModelSettings>(
      this.settingsKey,
      currentSettings,
      SettingTypes.Model,
    );

    this.storedSettingsService.setSettings<ModelViewSettings>(
      this.settingsKey,
      {
        id: this.currentJob.projectId,
        projectId: this.currentJob.projectId,
        height: this.height,
        depthHeight: this.depthHeight,
        iteration: 0,
        showDepthSlice: this.showDepthSliceGraph,
        ILXLStartDepth: this.ILXLStartDepth,
        ILXLEndDepth: this.ILXLEndDepth,
        ILXLStartInline: this.ILXLStartInline,
        ILXLEndInline: this.ILXLEndInline,
        ILXLStartCrossline: this.ILXLStartCrossline,
        ILXLEndCrossline: this.ILXLEndCrossline,
      },
      SettingTypes.ModelView,
    );
  }

  private persistDisplaySettings(isUpdatingModal?: boolean): void {
    this.settingsManager.persistSettings(
      this.currentModelTypeId,
      this.currentJob?.id,
      this.currentJob?.projectId,
      this.displaySettings,
      this.currentModelTypeProcessing,
      isUpdatingModal, // IMPORTANT: This is used to prevent the settings from being synchronised and applied to all stored settings when updating the modal
    );
  }

  private getAvailableTimeSlices(modelType: string): void {
    if (!this.shotModels.includes(modelType)) {
      return;
    }

    this.projectService
      .getShotsAvailableForModel(this.currentJob.id, this.iteration, modelType)
      .pipe(
        switchMap((shots) => {
          if (!shots || shots.length <= 0) {
            // If no shots, complete the stream with null
            return of(null);
          }

          shots.sort((a, b) => a - b);
          this.availShots = shots;
          if (!this.shotId) {
            this.shotId = shots[0];
          }

          if (!this.timeSliceModels.includes(modelType)) {
            // If not a time slice model, complete with null
            return of(null);
          }

          // Chain to the next API call
          return this.projectService.getTimeSliceAvailableForModel(
            this.currentJob.id,
            this.iteration,
            this.shotId,
            modelType,
          );
        }),
        take(1), // This ensures the stream completes after one emission.
        takeUntil(this.destroyed$), // Best practice for all component subscriptions.
      )
      .subscribe((timeSlices) => {
        if (!timeSlices) {
          this.availTimeSlices = []; // Ensure it's reset to an empty array
          return;
        }

        this.availTimeSlices = timeSlices;
        if (this.availTimeSlices.length > 0 && !this.timeSlice) {
          this.timeSlice = this.availTimeSlices[0];
        }
      });
  }

  onVpUpdateChange(event) {
    console.log("event on vpUpdateChange", event);
    this._forceDataReload();
  }

  onVpBockUpdateChange(rerender: boolean = false) {
    if (this.vpUpdateBlockCheck) {
      this.vpUpdateCheck = true;
      // resolve which block to go to
      var activeBlock: { [key: string]: any } = this.dataSource.find(
        (element) => {
          let [min_iter, max_iter]: number[] = element["iters"]["Iters"];
          if (this.iteration >= min_iter && this.iteration <= max_iter) {
            return element;
          }
        },
      );
      this.vpDiffIteration = activeBlock["iters"]["Iters"][0];
    }

    // this function is invoked in 2 places, iterationChange, and selfChange
    // from iterationChange, the rerendering will happen from that function itself
    // from selfChange, it should happen here
    if (rerender) {
      this._forceDataReload();
    }
  }

  onFullWidthILXLChange(event) {
    this.getSliceWidths();
    this._triggerRelayout(); // Just resize, no new data
    this.storeSettings();
  }

  onSliceDataReceived(event) {
    // Store data from crossline slice (sliceType === SliceType.crossLine) or inline slice if 2d job

    if (
      event.sliceType === SliceType.crossLine ||
      (event.sliceType === SliceType.inline && this.currentJob.modelGrid.is2d)
    ) {
      this.inlineSliceData = {
        sliceArray: event.sliceArray,
        trueMin: event.trueMin,
        trueMax: event.trueMax,
      };

      // Update vertical profile using the received data
      this.updateVerticalProfileFromSliceData();
    }
  }

  private updateVerticalProfileFromSliceData() {
    if (!this.inlineSliceData) return;

    this.verticalProfileIsLoading = false;
    this.verticalProfileHasError = false;

    let velocities = [];
    let crossline = this.currentJob.modelGrid.is2d
      ? this.velocityProfileCrossline
      : this.crosslineNumber;

    for (let i = 0; i < this.inlineSliceData.sliceArray.length; i++) {
      velocities.push(this.inlineSliceData.sliceArray[i][crossline - 1]);
    }

    this.velocities = velocities;
    let ranges = this.getRangesForSliceType(SliceType.inline);
    this.depth = ranges.y[0];
  }

  /**
   *
   * @description get the vertical profile i.e. the corresponding column in the slice array.
   */
  getVerticalProfile() {
    if (this.inlineSliceData) {
      // Use existing data if available
      this.updateVerticalProfileFromSliceData();
    } else {
      // Set loading state and wait for chart-slice to emit data
      this.verticalProfileIsLoading = true;
      this.verticalProfileHasError = false;
      this.velocities = [];
    }
  }

  /**
   *
   * @description get the functional sequence for the current job by reading runfile
   */
  getFunctionalSequence() {
    this.functional_sequence$ = this.projectService
      .getFunctionalSequence(
        this.currentJob.id
      )
      .pipe(
        take(1),
        switchMap((res) => {
        if (res) {
          let data = res.data
          let dataSource = [];
          this.functionals = [];
          this.frequencies = [];
          this.additionals = [];
          for (let i in data){
            let datum = data[i]
            let additionals_string = ""
            if (Object.keys(datum['additionals']).length > 0){
              // removes all additionals keys where value is absent, and then joins the remaining, surrounds them with '()'
              additionals_string = "(" + Object.entries(datum['additionals']).filter(([key, value]) => value !== null && value !== undefined).map(([key, value]) => `${key}: ${value}`).join(', ') + ")"
            }

            this.functionals.push(datum.functional);
            this.frequencies.push(datum.frequency);
            this.additionals.push(datum.additionals);
            // the additional string is added to iteratios or as additionals (which are added beside functional or iteration) depending on the functional
            // logic for WHERE the additional string is added is in the template
            let entry = {
              block: Number(i) + 1,
              block_started: datum.iterations[0] <= this.currentJob.iterationsComplete, 
              ...datum,
              additionals: additionals_string,
              iterations: datum['iterations'].join(' - ')
            };
            dataSource.push(entry);
          }
          this.dataSource = Array.from(dataSource);
          return of(dataSource);
        }
        }),
      );
  }

  onVerticalProfileAxisMinMaxUpdate(data) {
    this.zone.run(() => {
      this.verticalProfileAxis = data;
    });
  }

  onSyncAxisUpdate(data) {
    this.syncAxis = data;
  }

  /**
   *
   * @param event
   * @description under 2d, when the user click on the slice,
   * it emits the crossline and depth of the clicked point so that vertical profile can be updated accordingly.
   */
  onClickEmit(event) {
    if (this.currentJob.modelGrid.is2d && this.enableClick) {
      this.velocityProfileCrossline = event.crossline;
      this.depthSlice = event.depth;
      this.getVerticalProfile();
    }
  }

  /**
   *
   * @description swap the position of vertical profile and inline so that the vertical profile is on the right of the crossline
   * this is a bit hacky, maybe there's a better solution.
   */
  swapPosition() {
    const velocityProfileElement = document.getElementById("velocity-profile");
    const slices = document.getElementsByClassName("chart-slice");
    if (this.vpPositionSwapped) {
      const el = slices.item(1) as HTMLElement;
      el.style.order = "4";
      velocityProfileElement.style.order = "2";
      this.vpHeight = this.height;
    } else {
      const el = slices.item(1) as HTMLElement;
      el.style.order = "2";
      velocityProfileElement.style.order = "4";
      this.vpHeight = this.depthHeight;
    }
  }

  getVpBlock() {
    for (const item of this.dataSource) {
      const minIter = item["iters"]["Iters"][0];
      const maxIter = item["iters"]["Iters"][1];
      if (minIter <= this.iteration && maxIter >= this.iteration) {
        return minIter;
      }
    }

    return null;
  }

  /**
   *
   * @param event
   * @description when chart slice finish loading, it emits a ColorBarTextInfo object, which is caught here.
   * the min and max values are used to set the default x axis range for the vertical profile if the slice type is crossline or the job is 2d.
   */
  onColorbarTextEmit(event: ColorBarTextInfo) {
    // Store the true min/max for this slice type
    switch (event.sliceType) {
      case SliceType.inline:
        this.sliceRanges.inline = {
          min: event.min,
          max: event.max,
          trueMin: event.trueMin,
          trueMax: event.trueMax,
        };
        break;
      case SliceType.crossLine:
        this.sliceRanges.crossline = {
          min: event.min,
          max: event.max,
          trueMin: event.trueMin,
          trueMax: event.trueMax,
        };
        break;
      case SliceType.depth:
        this.sliceRanges.depth = {
          min: event.min,
          max: event.max,
          trueMin: event.trueMin,
          trueMax: event.trueMax,
        };
        break;
    }

    // If in auto mode, apply the ranges immediately
    if (this.displaySettings.isAuto) {
      this._applyAutoRangesFromSliceData();
    }
    this._forceDataReload(1000);
  }

  private _applyAutoRangesFromSliceData(): void {
    if (!this.displaySettings.isAuto) {
      return;
    }

    let updated = false;

    if (this.displaySettings.isAutoSynced) {
      if (
        this.displaySettings.min == null ||
        this.displaySettings.max == null
      ) {
        const ilxlRanges = [
          this.sliceRanges.inline,
          this.sliceRanges.crossline,
        ].filter((range) => !!range);
        if (ilxlRanges.length) {
          this.displaySettings.min = Math.min(
            ...ilxlRanges.map((r) => r.trueMin ?? r.min),
          );
          this.displaySettings.max = Math.max(
            ...ilxlRanges.map((r) => r.trueMax ?? r.max),
          );
          updated = true;
        }
      }
    }

    if (
      this.sliceRanges.depth &&
      (this.displaySettings.minZ == null || this.displaySettings.maxZ == null)
    ) {
      this.displaySettings.minZ = this.sliceRanges.depth.min;
      this.displaySettings.maxZ = this.sliceRanges.depth.max;
      updated = true;
    }

    if (updated) {
      this.defineCurrentVelocityRange();
      this.persistDisplaySettings();
    }
  }

  private _refreshAutoRanges(force: boolean = false): void {
    if (
      !this.displaySettings.isAuto ||
      !this.currentJob ||
      !this.currentModelTypeId
    ) {
      return;
    }

    const shouldFetchILXL =
      force ||
      (this.displaySettings.isAutoSynced &&
        (this.displaySettings.min == null || this.displaySettings.max == null));

    if (shouldFetchILXL) {
      this.ilxlRangeTrigger$.next();
    }

    const shouldFetchDepth =
      this.depthSlice != null &&
      (force ||
        this.displaySettings.minZ == null ||
        this.displaySettings.maxZ == null);

    if (shouldFetchDepth) {
      this.depthRangeTrigger$.next();
    }
  }

  private _buildSliceRange(
    min: number,
    max: number,
  ): {
    min: number;
    max: number;
    trueMin: number;
    trueMax: number;
  } {
    return { min, max, trueMin: min, trueMax: max };
  }

  private _getDialogRangeForSlice(type: SliceType): {
    min: number | null;
    max: number | null;
  } {
    if (type === SliceType.depth) {
      return { min: this.displaySettings.minZ, max: this.displaySettings.maxZ };
    }

    if (!this.displaySettings.isAuto || this.displaySettings.isAutoSynced) {
      return { min: this.displaySettings.min, max: this.displaySettings.max };
    }

    const range =
      type === SliceType.inline
        ? this.sliceRanges.crossline
        : this.sliceRanges.inline;

    if (range) {
      return {
        min: range.trueMin ?? range.min,
        max: range.trueMax ?? range.max,
      };
    }

    return { min: null, max: null };
  }

  private _shouldSendShotParam(): boolean {
    return this.currentModelTypeId
      ? this.shotModels.includes(this.currentModelTypeId)
      : false;
  }

  private _shouldSendTimeSliceParam(): boolean {
    return this.currentModelTypeId
      ? this.timeSliceModels.includes(this.currentModelTypeId)
      : false;
  }

  /**
   *
   * @description when chart slices finish loading, it emits event which is caught here.
   * when all slices finish loading, emit the event to scroll to the stored scroll position, in order to maintain the scroll when coming back to the page.
   */
  onLoadFinishedEmit(event) {
    if (this.waitingFor > 0) {
      this.waitingFor = this.waitingFor - 1;
      if (this.waitingFor == 0) {
        this.time_to_scroll_subscription.next("TIME TO SCROLL");
      }
    }
  }

  onInputHeightChange(event) {
    if (this.inputHeight <= 625 && this.inputHeight >= 250) {
      this.inputHeight = Math.round(this.inputHeight);
      this.height = this.inputHeight;
      this.setVpHeight();
    }
  }

  onInputDepthHeightChange(event) {
    if (
      this.inputDepthHeight <= this.maxDepth &&
      this.inputDepthHeight >= 200
    ) {
      this.inputDepthHeight = Math.round(this.inputDepthHeight);
      this.depthHeight = this.inputDepthHeight;
      this.setVpHeight();
    }
  }

  onKeySubmissionHeight(event) {
    if (event.key == "Enter") {
      if (this.inputHeight < 250) {
        this.inputHeight = 250;
      }
      if (this.inputHeight > 625) {
        this.inputHeight = 625;
      }
      this.height = Math.round(this.inputHeight);
      this.setVpHeight();
    }
  }

  onKeySubmissionDepthHeight(event) {
    if (event.key == "Enter") {
      if (this.inputDepthHeight < 200) {
        this.inputHeight = 200;
      }
      if (this.inputDepthHeight > this.maxDepth) {
        this.inputDepthHeight = this.maxDepth;
      }
      this.depthHeight = Math.round(this.inputDepthHeight);
      this.setVpHeight();
    }
  }

  setVpHeight() {
    this.vpHeight =
      this.currentJob.modelGrid.is2d || this.vpPositionSwapped
        ? this.height
        : this.depthHeight;
  }

  /**
   *
   * @description use html2canvas to draw the element with id = 'slices' to a canvas, then convert to a png data url and download
   */
  depthScreenshotToggle() {
    if (this.showDepthSliceScreenshot) {
      this.depthScreenshotText = "(w/ depth)";
    } else {
      this.depthScreenshotText = "(w/o depth)";
    }
  }

  /**
   *
   * @description toggle the height of the table that displays the functional sequence to be fixed or fit-content
   */
  toggleTableHeight() {
    if (this.tableHeight == "300px") {
      this.tableHeight = "fit-content";
    } else {
      let pos = document.body.clientHeight - this.scrollPos;
      this.tableHeight = "300px";
      setTimeout(() => {
        window.scrollTo({
          top: document.body.clientHeight - pos,
          left: 0,
          behavior: "smooth",
        });
      }, 0);
    }
  }

  toggleDepthSlice({ checked }: { checked: boolean }) {
    if (checked) {
      this.showDepthSlice();
    } else {
      this.hideDepthSlice();
    }
  }

  private showDepthSlice() {
    this.showDepthSliceScreenshot = false;
    this.depthScreenshotText = "(w/ depth)";

    this.slices.push({
      sliceType: SliceType.depth,
      span: 1,
    });

    this.showDepthSliceGraph = true;
  }

  private hideDepthSlice() {
    this.showDepthSliceScreenshot = true;
    this.depthScreenshotText = "(w/o depth)";

    const depthSlice = this.slices.find(
      (slice) => slice.sliceType === SliceType.depth,
    );
    if (!depthSlice) {
      return;
    }

    this.slices.splice(this.slices.indexOf(depthSlice), 1);

    this.showDepthSliceGraph = false;
  }

  private resetDepthSliceVisiblity(newShowDepthSliceValue?: boolean) {
    if (newShowDepthSliceValue === undefined) {
      this.hideDepthSlice();
      return;
    }

    // we only need to do anything if the visibility has changed from what we had before
    if (newShowDepthSliceValue && !this.showDepthSliceGraph) {
      this.showDepthSlice();
    }

    if (!newShowDepthSliceValue && this.showDepthSliceGraph) {
      this.hideDepthSlice();
    }
  }

  updateComment() {
    const details = {
      id: this.currentJob.id,
      name: this.currentJob.name,
      comments: this.comments,
      x2_is_inline: this.currentJob.x2_is_inline,
      owner: this.currentJob.owner
    };
    this.projectService.updateJobDetails(details).subscribe(
      (data) => {
        this.projectService.clearProjectsCache();
        this.projectService.clearJobDetailCache([this.currentJob.id]);
        this.snackbar.open("Job Details updated", null, {
          duration: 2000,
        });
      },
      (error) => {
        this.snackbar.open(
          "Sorry, the job details could not be updated",
          null,
          { duration: 2000 },
        );
      },
    );
  }

  private isSafari(): boolean {
    return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  }

  // Safari-specific chart rendering wait
  private async waitForSafariChartRendering(): Promise<void> {
    // Only apply this fix for Safari
    if (!this.isSafari()) {
      return;
    }

    // Wait for Safari's rendering pipeline to complete
    await new Promise<void>((resolve) => {
      // Use requestAnimationFrame to wait for paint cycles
      // The window.requestAnimationFrame() method tells the browser you wish to perform an animation.
      // It requests the browser to call a user-supplied callback function before the next repaint.

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Additional delay for Safari's compositor
          setTimeout(() => {
            resolve();
          }, 300);
        });
      });
    });
  }

  async screenshotPage() {
    await this.waitForSafariChartRendering();

    const printDiv = document.createElement("div");

    const element = document.getElementById("slices");

    const container = document.getElementById(
      "screenshot-container-div",
    ) as HTMLElement;
    const inlineDiv = element
      .querySelector("#slice_id_0")
      .cloneNode(true) as HTMLElement;
    inlineDiv.id = "_print_1";
    inlineDiv.style.order = "1";
    inlineDiv.style.color = "white";

    const crosslineDiv = element
      .querySelector("#slice_id_1")
      .cloneNode(true) as HTMLElement;
    crosslineDiv.id = "_print_2";
    crosslineDiv.style.order = "2";
    crosslineDiv.style.color = "white";

    printDiv.style.display = "flex";
    printDiv.style.flexWrap = "wrap";
    printDiv.style.background = "#f5f5f5";
    printDiv.style.width = "100%";
    printDiv.style.marginBottom = "10px";
    printDiv.appendChild(inlineDiv);

    if (crosslineDiv.children.length != 0) {
      // if both inline and crossline exist, show the inline crossline side by side
      printDiv.appendChild(crosslineDiv);
    }

    const vertProfileDiv =
      (document
        .getElementById("velocity-profile")
        ?.cloneNode(true) as HTMLElement) || null;
    const depthDiv =
      (element.querySelector("#slice_id_2")?.cloneNode(true) as HTMLElement) ||
      null;
    if (this.showDepthSlice && depthDiv) {
      depthDiv.id = "_print_3";
      printDiv.appendChild(depthDiv);
      depthDiv.style.order = "3";
      depthDiv.style.color = "white";
    }
    if (this.screenshotWithVertProfile && vertProfileDiv) {
      vertProfileDiv.id = "_print_4";
      printDiv.appendChild(vertProfileDiv);
      vertProfileDiv.style.order = "4";
      vertProfileDiv.style.color = "white";
    }

    if (this.vpPositionSwapped) {
      vertProfileDiv.style.order = "2";
      crosslineDiv.style.order = "3";
      if (depthDiv) {
        depthDiv.style.order = "4";
      }
    }

    container.appendChild(printDiv);

    // Use html-to-image to take screenshot
    htmlToImage
      .toPng(printDiv, {
        quality: 1.0,
        pixelRatio: window.devicePixelRatio,
      })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.href = dataUrl;
        let name;
        let zoomlevel = window.devicePixelRatio;
        if (this.currentJob.modelGrid.is2d) {
          name =
            stripDash(this.currentJob.projectName.substring(0, 7)) +
            "-" +
            stripDash(this.currentJob.name.substring(0, 7)) +
            "-CPNUM" +
            this.iteration +
            "-" +
            this.currentModelTypeId +
            "-Height" +
            this.height +
            "-ZOOM" +
            zoomlevel +
            "-" +
            this.currentJob.id +
            ".png";
        } else {
          name =
            stripDash(this.currentJob.projectName.substring(0, 7)) +
            "-" +
            stripDash(this.currentJob.name.substring(0, 7)) +
            "-IL" +
            this.inlineNumber.toString() +
            "-XL" +
            this.crosslineNumber.toString() +
            "-DEP" +
            this.depthSlice.toString() +
            "-CPNUM" +
            this.iteration +
            "-" +
            this.currentModelTypeId +
            "-Height" +
            this.height +
            "_" +
            this.depthHeight +
            "-ZOOM" +
            zoomlevel +
            "-" +
            this.currentJob.id +
            ".png";
        }
        link.download = name;
        link.click();

        container.removeChild(printDiv);
      })
      .catch((error) => {
        console.error("Error generating screenshot:", error);
        container.removeChild(printDiv);
      });
  }

  async downloadSlicesAsPng() {
    // Wait for Safari chart rendering if needed
    await this.waitForSafariChartRendering();

    const velocity_profile = document.getElementById("velocity-profile");
    const model_info = document.getElementById("_model_information_container");
    if (!this.screenshotWithVertProfile) {
      if (velocity_profile != null) {
        velocity_profile.style.display = "none";
      }
    }

    model_info.style.display = "none";

    const element = document.getElementById("slices");
    // Deselect the comment section
    const comment_section = document.getElementById("comment");
    let commentDisplay = comment_section.style.display;
    comment_section.style.display = "none";

    let current_color = element.style.backgroundColor;
    element.style.backgroundColor = "#F3F3F3";

    // Use html-to-image to take screenshot
    htmlToImage
      .toPng(element, {
        quality: 1.0,
        pixelRatio: window.devicePixelRatio,
        filter: (node) => {
          // Equivalent to ignoreElements in html2canvas
          if (node instanceof Element) {
            return !(
              node.classList.contains("chart-controls") ||
              node.classList.contains("zmdi-more-vert") ||
              node.classList.contains("zmdi-download") ||
              node.classList.contains("actions")
            );
          }
          return true;
        },
      })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.href = dataUrl;
        let name;
        let zoomlevel = window.devicePixelRatio;
        if (this.currentJob.modelGrid.is2d) {
          name =
            stripDash(this.currentJob.projectName.substring(0, 7)) +
            "-" +
            stripDash(this.currentJob.name.substring(0, 7)) +
            "-CPNUM" +
            this.iteration +
            "-" +
            this.currentModelTypeId +
            "-Height" +
            this.height +
            "-ZOOM" +
            zoomlevel +
            "-" +
            this.currentJob.id +
            ".png";
        } else {
          name =
            stripDash(this.currentJob.projectName.substring(0, 7)) +
            "-" +
            stripDash(this.currentJob.name.substring(0, 7)) +
            "-IL" +
            this.inlineNumber.toString() +
            "-XL" +
            this.crosslineNumber.toString() +
            "-DEP" +
            this.depthSlice.toString() +
            "-CPNUM" +
            this.iteration +
            "-" +
            this.currentModelTypeId +
            "-Height" +
            this.height +
            "_" +
            this.depthHeight +
            "-ZOOM" +
            zoomlevel +
            "-" +
            this.currentJob.id +
            ".png";
        }
        link.download = name;
        link.click();

        // Restore changes
        element.style.backgroundColor = current_color;
        comment_section.style.display = commentDisplay;
        model_info.style.display = "";
        if (!this.screenshotWithVertProfile) {
          if (velocity_profile != null) {
            velocity_profile.style.display = "block";
          }
        }
      })
      .catch((error) => {
        console.error("Error generating screenshot:", error);
        // Restore changes even on error
        element.style.backgroundColor = current_color;
        comment_section.style.display = commentDisplay;
        model_info.style.display = "";
        if (!this.screenshotWithVertProfile) {
          if (velocity_profile != null) {
            velocity_profile.style.display = "block";
          }
        }
      });
  }

  syntaxHighlight(jsonObj: object) {
    let json = JSON.stringify(jsonObj, null, 2);
    json = json
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
    let temp = json.replace(
      /("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
      function (match) {
        var cls = "number";
        var sty = "color: darkorange;";
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = "key";
            sty = "color: red;";
          } else {
            cls = "string";
            sty = "color: green;";
          }
        } else if (/true|false/.test(match)) {
          cls = "boolean";
          sty = "color: blue;";
        } else if (/null/.test(match)) {
          cls = "null";
          sty = "color: magenta;";
        }
        return (
          '<span style="' + sty + '" class="' + cls + '">' + match + "</span>"
        );
      },
    );
    return `<pre>${temp}</pre>`;
  }

  getModelInformation() {
    this.modelInfoLoading = true;
    this.modelInfoFetched = true;
    this.projectService
      .getModelInformation(this.currentJob.id, this.iteration)
      .toPromise()
      .then((res) => {
        document.getElementById("_model_information").innerHTML =
          this.syntaxHighlight(res);
      })
      .catch((err) => {
        console.log("error", err);
        document.getElementById("_model_information").innerHTML =
          this.syntaxHighlight({
            error: "No relevant EBCDIC data for this job and iteration",
          });
      })
      .finally(() => (this.modelInfoLoading = false));
  }

  clearModelInfo() {
    this.modelInfoFetched = false;
    if (document.getElementById("_model_information")?.innerHTML) {
      document.getElementById("_model_information").innerHTML = "";
    }
  }

  reparseJob() {
    console.log("reparsing current job", this.currentJob.id);

    this.reparsingJob = true;
    this.notificationService.info(
      "Reparsing Job",
      "Please wait while the job is being reparsed...",
    );

    let entries = [
      {
        path: `/${this.currentJob.basePath}`,
        parent_job_id: this.currentJob.parent_job_id,
        parent_cp_num: this.currentJob.parent_cp_num,
        x2_is_inline: this.currentJob.x2_is_inline,
        is_update: true,
      },
    ];

    this.projectService
      .createJobs(this.projectData.id, entries)
      .pipe(take(1))
      .toPromise()
      .then((res) => {
        res["failed"].forEach((element) => {
          this.notificationService.error(
            "Error",
            `Job with path: <div>${element["path"]} </div> <br> failed to be added to the project: <br><b>${element["error"]}</b>`,
          );
        });

        res["passed"].forEach((element) => {
          let warnings = element["warnings"] || [];
          warnings.forEach((warning: string) => {
            this.notificationService.warning("Warning", warning);
          });
          this.notificationService.success(
            "Success",
            `Job <div><b>${element["path"]}</b></div> was successfully re-parsed`,
          );
        });

        // clear cache and refetch
        this.projectService.clearProjectsCache();
        this.projectService.clearJobDetailCache([this.currentJob.id]);

        this.projectService
          .getJobDetail(this.currentJob.id, true)
          .pipe(take(1))
          .subscribe(
            (updatedJob) => {
              this.currentJob = updatedJob;
              // update model type list just in case
              this.updateModelTypeList();
              this.reparsingJob = false;
            },
            (error) => {
              console.error("Error refetching job details:", error);
              this.reparsingJob = false;
            },
          );
      })
      .catch((err) => {
        this.notificationService.error(
          "Error",
          err.error?.error || "An unexpected error occured",
        );
        this.isLoading = false;
        this.reparsingJob = false;
      });
  }

  scrollToAnchor(anchor: string) {
    // this.scroller.scrollToAnchor(anchor);
    const element = document.getElementById(anchor);
    if (element) {
      const headerOffset = 50; // Change this to whatever looks best
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition =
        elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    }
  }

  trackBySliceId(index: number, slice: any): string {
    return slice.sliceType; // Or whatever unique property your 'slice' object has
  }
}

export function getRandomHexColor(index: number = null): string {
  const first10Colors = [
    "#800080", // purple
    "#00FFFF", // cyan
    "#000000", // black
    "#FFFF00", // yellow
    "#FF00FF", // magenta
    "#FFA500", // orange
    "#00FF00", // green / lime
    "#0000FF", // blue
    "#FFFFFF", // white
    "#FF0000", // red
  ];

  if (index !== null && index < 10) {
    return first10Colors[index];
  }

  const hexChars = "0123456789ABCDEF";
  let color = "#";

  // Generate a random six-digit hexadecimal color code
  for (let i = 0; i < 6; i++) {
    color += hexChars[Math.floor(Math.random() * 16)];
  }

  return color;
}
